
import { useState, useCallback } from 'react';
import { sanitizeProgramsForSave } from '../utils/planDataUtils';
import { fetchWithAuth } from '../utils/fetchWithAuth';

export const usePlanState = (userDetails, currentPlanId, setCurrentPlanId) => {
  // Plan state variables
  const [currentDesignChallenge, setCurrentDesignChallenge] = useState("");
  const [planName, setPlanName] = useState("");
  const [shortSummary, setShortSummary] = useState("");
  const [problemStatement, setProblemStatement] = useState("");
  const [goalStatement, setGoalStatement] = useState("");
  const [targetStatement, setTargetStatement] = useState("");
  const [objectivesStatement, setObjectivesStatement] = useState([]);
  const [deliverablesStatement, setDeliverablesStatement] = useState("");
  const [metricsStatement, setMetricsStatement] = useState("");
  const [stakeholdersCustomersInvolvement, setStakeholdersCustomersInvolvement] = useState("");
  const [experienceDesignPrinciples, setExperienceDesignPrinciples] = useState("");
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [finalProgramSequence, setFinalProgramSequence] = useState([]);
  const [totalDuration, setTotalDuration] = useState(0);
  const [enabledFeatures, setEnabledFeatures] = useState(null); // Store enabled capabilities
  const [currentWorkflowId, setCurrentWorkflowId] = useState(null); // Store workflow ID for feature derivation
  
  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(true);
  const [planState, setPlanState] = useState("idle");
  
  // Save plan function - now accepts optional direct program and strategy data and workflow context
  const savePlan = useCallback(async (directProgramData = null, workflowContext = null) => {
    if (!userDetails?.id) {
      console.log("No user details available, skipping save");
      return null;
    }

    // Use direct program data if provided, otherwise fall back to state
    const programsToSave = directProgramData?.programs || selectedPrograms || [];
    const finalSequenceToSave = directProgramData?.finalSequence || finalProgramSequence || [];
    
    // Use direct strategy data if provided, otherwise fall back to state
    const strategyData = directProgramData?.strategyData;
    const strategyFieldsToSave = strategyData ? {
      planName: strategyData.planName || planName,
      shortSummary: strategyData.shortSummary || shortSummary,
      problemStatement: strategyData.problemStatement || problemStatement,
      goalStatement: strategyData.goalStatement || goalStatement,
      targetStatement: strategyData.targetStatement || targetStatement,
      objectivesStatement: strategyData.objectives || objectivesStatement,
      deliverablesStatement: strategyData.deliverables || deliverablesStatement,
      metricsStatement: strategyData.metrics || metricsStatement,
      stakeholdersCustomersInvolvement: Array.isArray(strategyData.stakeholders) 
        ? strategyData.stakeholders.join('\n') 
        : strategyData.stakeholders || stakeholdersCustomersInvolvement,
      experienceDesignPrinciples: Array.isArray(strategyData.experienceDesignPrinciples) 
        ? strategyData.experienceDesignPrinciples.join('\n') 
        : (strategyData.experienceDesignPrinciples || experienceDesignPrinciples),
      totalDuration: strategyData.totalDuration || totalDuration,
    } : {
      planName,
      shortSummary,
      problemStatement,
      goalStatement,
      targetStatement,
      objectivesStatement,
      deliverablesStatement,
      metricsStatement,
      stakeholdersCustomersInvolvement,
      experienceDesignPrinciples,
      totalDuration,
    };

    console.log("🔍 savePlan called:", {
      usingDirectData: !!directProgramData,
      usingDirectStrategyData: !!strategyData,
      programsToSaveCount: programsToSave?.length || 0,
      finalSequenceToSaveCount: finalSequenceToSave?.length || 0,
      selectedProgramsCount: selectedPrograms?.length || 0,
      finalSequenceCount: finalProgramSequence?.length || 0,
      strategyDataPreview: {
        hasShortSummary: !!strategyFieldsToSave.shortSummary,
        shortSummaryLength: strategyFieldsToSave.shortSummary?.length || 0,
        hasProblemStatement: !!strategyFieldsToSave.problemStatement,
        problemStatementLength: strategyFieldsToSave.problemStatement?.length || 0,
        hasGoalStatement: !!strategyFieldsToSave.goalStatement,
        goalStatementLength: strategyFieldsToSave.goalStatement?.length || 0
      },
      programsPreview: programsToSave?.slice(0, 1).map(p => ({
        name: p?.program_of_work || p?.name,
        hasToolsByStage: !!p?.toolsByStage
      })),
      finalSequencePreview: finalSequenceToSave?.slice(0, 1).map(p => ({
        name: p?.program_of_work || p?.name,
        hasToolsByStage: !!p?.toolsByStage
      }))
    });

    setIsSaving(true);
    setPlanState("saving");

    try {
      const sanitizedSelectedPrograms = sanitizeProgramsForSave(programsToSave || []);
      const sanitizedFinalSequence = sanitizeProgramsForSave(finalSequenceToSave || []);
      
      console.log("🔍 After sanitization:", {
        originalSelected: selectedPrograms?.length || 0,
        sanitizedSelected: sanitizedSelectedPrograms?.length || 0,
        originalFinal: finalProgramSequence?.length || 0,
        sanitizedFinal: sanitizedFinalSequence?.length || 0
      });

      const calculatedTotalDuration = sanitizedFinalSequence.reduce(
        (sum, program) => {
          return sum + (program.totalProgramLength || program.duration || 0);
        },
        0,
      );

      // Determine enabled features: use direct context or state
      const featuresToSave = workflowContext?.enabledFeatures || enabledFeatures;
      // A normal UI save (including Brief auto-save) must persist the current
      // Brief fields. Background workflow saves that provide only program data
      // remain blocked from overwriting the Brief with potentially stale state.
      const canSaveBriefFields =
        !currentPlanId ||
        !directProgramData ||
        !!strategyData ||
        workflowContext?.allowBriefUpdate === true;
      
      const truncate = (str, max = 60) =>
        str && str.length > max ? str.substring(0, max).trim() + "…" : str;

      let derivedPlanName = "";
      if (strategyFieldsToSave.planName) {
        derivedPlanName = truncate(strategyFieldsToSave.planName);
      } else if (!currentPlanId) {
        derivedPlanName = truncate(strategyFieldsToSave.shortSummary);
        if (!derivedPlanName && currentDesignChallenge) {
          derivedPlanName = truncate(currentDesignChallenge, 50);
        }
        if (!derivedPlanName) {
          derivedPlanName = "Unnamed Plan";
        }
      }
      
      const planData = {
        user_id: userDetails.id,
        plan_name: canSaveBriefFields ? derivedPlanName : null,
        design_challenge: canSaveBriefFields ? currentDesignChallenge : null,
        short_summary: canSaveBriefFields ? strategyFieldsToSave.shortSummary : null,
        problem_statement: canSaveBriefFields ? strategyFieldsToSave.problemStatement : null,
        goal_statement: canSaveBriefFields ? strategyFieldsToSave.goalStatement : null,
        target_statement: canSaveBriefFields ? strategyFieldsToSave.targetStatement : null,
        objectives_statement: canSaveBriefFields ? strategyFieldsToSave.objectivesStatement : null,
        deliverables_statement: canSaveBriefFields ? strategyFieldsToSave.deliverablesStatement : null,
        metrics_statement: canSaveBriefFields ? strategyFieldsToSave.metricsStatement : null,
        stakeholders_customers_involvement: canSaveBriefFields ? strategyFieldsToSave.stakeholdersCustomersInvolvement : null,
        experience_design_principles: canSaveBriefFields ? strategyFieldsToSave.experienceDesignPrinciples : null,
        selected_programs: JSON.stringify(sanitizedSelectedPrograms),
        final_program_sequence: JSON.stringify(sanitizedFinalSequence),
        total_duration: calculatedTotalDuration || strategyFieldsToSave.totalDuration,
        enabled_features: featuresToSave ? JSON.stringify(featuresToSave) : null,
        workflow_id: currentWorkflowId,
      };
      
      console.log("🔍 Saving workflow_id:", currentWorkflowId, "| enabled_features:", featuresToSave);

      console.log("🔍 Final plan data being saved:", {
        hasShortSummary: !!planData.short_summary,
        shortSummaryLength: planData.short_summary?.length || 0,
        hasProblemStatement: !!planData.problem_statement,
        problemStatementLength: planData.problem_statement?.length || 0,
        hasGoalStatement: !!planData.goal_statement,
        goalStatementLength: planData.goal_statement?.length || 0,
        hasTargetStatement: !!planData.target_statement,
        targetStatementLength: planData.target_statement?.length || 0,
        hasExperienceDesignPrinciples: !!planData.experience_design_principles,
        experienceDesignPrinciplesLength: planData.experience_design_principles?.length || 0,
        programsCount: sanitizedSelectedPrograms?.length || 0,
        finalSequenceCount: sanitizedFinalSequence?.length || 0
      });

      let response, result;
      let planId = currentPlanId;

      if (currentPlanId) {
        response = await fetchWithAuth(`/api/plans/${currentPlanId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(planData),
        });
        result = await response.json();
      } else {
        response = await fetchWithAuth("/api/plans/save-plan", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(planData),
        });
        result = await response.json();
        planId = result.plan_id;
      }

      if (response.ok) {
        console.log("🔥🔥🔥 usePlanState.savePlan: Save successful, returning planId:", planId || result.plan_id);
        // Only update currentPlanId for NEW plans (when currentPlanId is null/undefined)
        // This prevents triggering useEffect in PlanPage that reloads the plan
        if (result.plan_id && !currentPlanId) {
          // Defer setCurrentPlanId if caller handles it (e.g. create_plan needs PS saved first)
          if (!directProgramData?.deferSetCurrentPlanId) {
            setCurrentPlanId(result.plan_id);
          }
          planId = result.plan_id;
          
          // Update URL without page reload for new plans.
          // React Router v6 stores location.state under window.history.state.usr.
          // We must preserve the entire history state so React Router's internal
          // key/idx fields survive, and so _cacheKey stays intact (keeping the
          // EmbeddedChatPanel key stable → no remount → messages preserved).
          // Strip initiateStrategyCreation so a hard reload won't re-trigger the workflow.
          const newUrl = `/plan/${result.plan_id}`;
          const currentHistoryState = window.history.state || {};
          const currentUserState = currentHistoryState.usr || {};
          const newHistoryState = {
            ...currentHistoryState,
            usr: {
              ...currentUserState,
              initiateStrategyCreation: false,  // clear launch flag for future reloads
            },
          };
          window.history.replaceState(newHistoryState, '', newUrl);
          console.log("✅ URL updated to:", newUrl);
        }
        setIsSaved(true);
        setPlanState("saved");
        console.log("🔥🔥🔥 usePlanState.savePlan: About to return planId:", planId);
        return planId;
      } else {
        console.error("Failed to save the plan:", result);
        setPlanState("error");
        return null;
      }
    } catch (error) {
      console.error("Error saving the plan:", error);
      setPlanState("error");
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [
    userDetails?.id,
    currentPlanId,
    currentDesignChallenge,
    planName,
    shortSummary,
    problemStatement,
    goalStatement,
    targetStatement,
    objectivesStatement,
    deliverablesStatement,
    metricsStatement,
    selectedPrograms,
    finalProgramSequence,
    totalDuration,
    setCurrentPlanId,
    stakeholdersCustomersInvolvement,
    experienceDesignPrinciples,
  ]);

  return {
    // State
    currentDesignChallenge, setCurrentDesignChallenge,
    planName, setPlanName,
    shortSummary, setShortSummary,
    problemStatement, setProblemStatement,
    goalStatement, setGoalStatement,
    targetStatement, setTargetStatement,
    objectivesStatement, setObjectivesStatement,
    deliverablesStatement, setDeliverablesStatement,
    metricsStatement, setMetricsStatement,
    stakeholdersCustomersInvolvement, setStakeholdersCustomersInvolvement,
    experienceDesignPrinciples, setExperienceDesignPrinciples,
    selectedPrograms, setSelectedPrograms,
    finalProgramSequence, setFinalProgramSequence,
    totalDuration, setTotalDuration,
    enabledFeatures, setEnabledFeatures, // Capability configuration
    currentWorkflowId, setCurrentWorkflowId, // Workflow ID for feature derivation
    isSaving, setIsSaving,
    isSaved, setIsSaved,
    planState, setPlanState,
    // Actions
    savePlan,
  };
};
